import React from 'react';
import axios from 'axios';

// Msg #994 — Admin popup: view a user's live rank/HP/MP/Atk/Def and edit them.
//              Also: admin popup to view + set an NPC's affection / dislike
//              toward a specific user.
//
// These are strictly admin-only tools. They mount above every other modal
// (z-index 1400). Both call `/api/admin/*` endpoints and never trust the
// admin role from the client — the backend enforces `_require_admin`.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const overlay = {
  position: 'fixed', inset: 0, background: 'rgba(8,12,8,0.7)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400,
};
const shell = {
  background: '#f2e8c8', border: '3px double #7a5a1a',
  boxShadow: '0 12px 30px rgba(0,0,0,0.6)',
  width: 'min(96vw, 520px)', maxHeight: '90vh', overflowY: 'auto',
  padding: 18, fontFamily: 'Georgia, "Times New Roman", serif',
  color: '#3a2b14', position: 'relative',
};
const closeX = {
  position: 'absolute', top: 6, right: 8,
  width: 26, height: 26, background: '#c0392b', color: '#fff',
  border: '1px solid #7a1f14', borderRadius: 4,
  fontWeight: 'bold', fontSize: 14, cursor: 'pointer',
};
const row = { display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' };
const label = { minWidth: 130, fontSize: 12, fontWeight: 'bold' };
const input = { flex: 1, padding: '4px 6px', border: '1px solid #5a4a1d', fontFamily: 'inherit', fontSize: 12 };
const readonlyPill = {
  padding: '2px 8px', background: '#e0d3a3',
  border: '1px solid #7a5a1a', fontSize: 11, borderRadius: 4,
  marginRight: 4, fontWeight: 'bold',
};

// ────────────────────────────────────────────────────────────────────
// Admin: View + Edit Character Stats
// ────────────────────────────────────────────────────────────────────
export const AdminEditUserStatsModal = ({ open, onClose, requester }) => {
  const [target, setTarget] = React.useState('');
  const [current, setCurrent] = React.useState(null); // { rank, kills, hp, hp_max, mp, mp_max, attack, defense, overrides }
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [note, setNote] = React.useState('');

  const [killsDelta, setKillsDelta] = React.useState('0');
  const [hpMax, setHpMax] = React.useState('');
  const [mpMax, setMpMax] = React.useState('');
  const [atk, setAtk] = React.useState('');
  const [def, setDef] = React.useState('');
  const [healFull, setHealFull] = React.useState(true);

  React.useEffect(() => {
    if (!open) return;
    setTarget(''); setCurrent(null); setErr(''); setNote('');
    setKillsDelta('0'); setHpMax(''); setMpMax('');
    setAtk(''); setDef(''); setHealFull(true);
  }, [open]);

  const fetchStats = React.useCallback(async (name) => {
    const tn = (name || '').trim();
    if (!tn) return;
    setLoading(true); setErr(''); setNote('');
    try {
      const [rankR, hpR] = await Promise.all([
        axios.get(`${API}/combat/user_stats`, { params: { username: tn } }),
        axios.get(`${API}/user/hp`, { params: { user_id: '', username: tn } }).catch(() => ({ data: {} })),
      ]);
      const rk = rankR.data || {};
      const hp = hpR.data || {};
      setCurrent({
        rank: rk.rank || 'Novice',
        rank_icon: rk.rank_icon || '🥉',
        kills: rk.kills || 0,
        hp: hp.hp ?? rk.hp_max ?? 10,
        hp_max: rk.hp_max ?? hp.hp_max ?? 10,
        mp: hp.mp ?? rk.mp_max ?? 0,
        mp_max: rk.mp_max ?? hp.mp_max ?? 0,
        attack: rk.attack ?? 1,
        defense: rk.defense ?? 1,
        damage_dealt: rk.damage_dealt ?? 0,
      });
      // Prefill the override inputs with the current values so the admin
      // can nudge from a known baseline instead of a blank form.
      setHpMax(String(rk.hp_max ?? ''));
      setMpMax(String(rk.mp_max ?? ''));
      setAtk(String(rk.attack ?? ''));
      setDef(String(rk.defense ?? ''));
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Failed to load stats. Has this user logged in yet?');
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const parseIntOr = (s) => {
    if (s === '' || s == null) return null;
    const v = parseInt(s, 10);
    return Number.isFinite(v) ? v : null;
  };

  const save = async () => {
    if (!target.trim()) { setErr('Enter a username first.'); return; }
    setBusy(true); setErr(''); setNote('');
    try {
      const r = await axios.post(`${API}/admin/user_stats`, {
        requester,
        target: target.trim(),
        kills_delta: parseInt(killsDelta || '0', 10) || 0,
        hp_max_override: parseIntOr(hpMax),
        mp_max_override: parseIntOr(mpMax),
        attack_override: parseIntOr(atk),
        defense_override: parseIntOr(def),
        heal_full: healFull,
      });
      setNote(`Saved. Rank now: ${r.data?.rank?.rank || '(unknown)'} — ${r.data?.kills ?? 0} kills.`);
      setKillsDelta('0');
      await fetchStats(target);
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div style={overlay} data-testid="admin-edit-user-stats-modal" onClick={onClose}>
      <div style={shell} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="admin-user-stats-close"
          style={closeX}
        >✕</button>
        <div style={{ fontSize: 18, fontWeight: 'bold', textTransform: 'uppercase',
                      letterSpacing: 2, marginBottom: 4 }}>
          ⚡ Character Stats — View &amp; Edit
        </div>
        <div style={{ fontSize: 11, marginBottom: 12, fontStyle: 'italic', opacity: 0.85 }}>
          Enter a username and press LOAD. You'll see the current rank, HP, MP,
          Atk, Def, then override any of them and click SAVE.
        </div>

        <div style={row}>
          <span style={label}>Target username</span>
          <input
            data-testid="admin-user-stats-target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchStats(target); }}
            placeholder="e.g. testuser"
            style={input}
            autoFocus
          />
          <button
            data-testid="admin-user-stats-load"
            onClick={() => fetchStats(target)}
            disabled={loading || !target.trim()}
            style={{
              background: '#4a3a1a', color: '#ffe1a0',
              border: '1px solid #a08a30', padding: '4px 14px',
              fontWeight: 'bold', cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >{loading ? '…' : 'LOAD'}</button>
        </div>

        {current && (
          <div
            data-testid="admin-user-stats-view"
            style={{
              margin: '8px 0 14px', padding: 10,
              background: '#faf3d8', border: '1px solid #a08a30',
              borderRadius: 4,
            }}
          >
            <div style={{ marginBottom: 6, fontSize: 13 }}>
              <span style={{ fontSize: 18, marginRight: 4 }}>{current.rank_icon}</span>
              <b data-testid="admin-user-stats-rank">{current.rank}</b>
              <span style={{ marginLeft: 8, fontSize: 11, opacity: 0.7 }}>
                ({current.kills} kills, {current.damage_dealt} damage dealt)
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              <span style={readonlyPill} data-testid="admin-user-stats-hp">
                ❤ HP <b>{current.hp}/{current.hp_max}</b>
              </span>
              <span style={readonlyPill} data-testid="admin-user-stats-mp">
                🔮 MP <b>{current.mp}/{current.mp_max}</b>
              </span>
              <span style={readonlyPill} data-testid="admin-user-stats-atk">
                ⚔ Atk <b>+{current.attack}</b>
              </span>
              <span style={readonlyPill} data-testid="admin-user-stats-def">
                🛡 Def <b>+{current.defense}</b>
              </span>
            </div>
          </div>
        )}

        <div style={{ borderTop: '1px dashed #7a5a1a', paddingTop: 10, marginTop: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
            Edit
          </div>
          <div style={row}>
            <span style={label}>Kills (delta)</span>
            <input
              data-testid="admin-user-stats-kills-delta"
              type="number" value={killsDelta}
              onChange={(e) => setKillsDelta(e.target.value)}
              placeholder="e.g. 1 or -2"
              style={input}
            />
          </div>
          <div style={row}>
            <span style={label}>HP max</span>
            <input
              data-testid="admin-user-stats-hp-max-input"
              type="number" value={hpMax}
              onChange={(e) => setHpMax(e.target.value)}
              placeholder="leave blank = no change"
              style={input}
            />
          </div>
          <div style={row}>
            <span style={label}>MP max</span>
            <input
              data-testid="admin-user-stats-mp-max-input"
              type="number" value={mpMax}
              onChange={(e) => setMpMax(e.target.value)}
              placeholder="leave blank = no change"
              style={input}
            />
          </div>
          <div style={row}>
            <span style={label}>Attack</span>
            <input
              data-testid="admin-user-stats-atk-input"
              type="number" value={atk}
              onChange={(e) => setAtk(e.target.value)}
              placeholder="leave blank = no change"
              style={input}
            />
          </div>
          <div style={row}>
            <span style={label}>Defense</span>
            <input
              data-testid="admin-user-stats-def-input"
              type="number" value={def}
              onChange={(e) => setDef(e.target.value)}
              placeholder="leave blank = no change"
              style={input}
            />
          </div>
          <div style={row}>
            <label style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                data-testid="admin-user-stats-heal-full"
                type="checkbox"
                checked={healFull}
                onChange={(e) => setHealFull(e.target.checked)}
              />
              Heal HP + MP to full after edit
            </label>
          </div>
        </div>

        {err && (
          <div data-testid="admin-user-stats-error"
               style={{ color: '#7a1f1f', fontSize: 12, marginTop: 6 }}>{err}</div>
        )}
        {note && (
          <div data-testid="admin-user-stats-note"
               style={{ color: '#1c5a1c', fontSize: 12, marginTop: 6 }}>{note}</div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button
            onClick={onClose}
            style={{
              background: '#5a4a1d', color: '#f5e6c0',
              border: '1px solid #3a2b14', padding: '4px 14px',
              fontWeight: 'bold', cursor: 'pointer',
            }}
          >Close</button>
          <button
            data-testid="admin-user-stats-save"
            onClick={save}
            disabled={busy || !target.trim()}
            style={{
              background: '#4a3a1a', color: '#ffe1a0',
              border: '1px solid #a08a30', padding: '4px 14px',
              fontWeight: 'bold', cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >{busy ? 'Saving…' : 'SAVE'}</button>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
// Admin: View + Edit NPC affection / dislike toward a specific user
// ────────────────────────────────────────────────────────────────────
export const AdminEditNpcAffectionModal = ({ open, onClose, requester }) => {
  const [target, setTarget] = React.useState('');
  const [npc, setNpc] = React.useState('');
  const [current, setCurrent] = React.useState(null); // {affection, dislike, romance_unlocked}
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [note, setNote] = React.useState('');
  const [affection, setAffection] = React.useState('');
  const [dislike, setDislike] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setTarget(''); setNpc('');
    setCurrent(null); setErr(''); setNote('');
    setAffection(''); setDislike('');
  }, [open]);

  const load = React.useCallback(async () => {
    if (!target.trim() || !npc.trim()) {
      setErr('Enter both a username and an NPC name.');
      return;
    }
    setLoading(true); setErr(''); setNote('');
    try {
      const r = await axios.get(`${API}/admin/npc_affection`, {
        params: { requester, target: target.trim(), npc: npc.trim() },
      });
      setCurrent({
        affection: r.data?.affection ?? 0,
        dislike: r.data?.dislike ?? 0,
        romance_unlocked: !!r.data?.romance_unlocked,
        canonical_target: r.data?.target,
        npc_key: r.data?.npc,
      });
      setAffection(String(r.data?.affection ?? 0));
      setDislike(String(r.data?.dislike ?? 0));
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Failed to load.');
      setCurrent(null);
    } finally {
      setLoading(false);
    }
  }, [target, npc, requester]);

  const save = async () => {
    if (!target.trim() || !npc.trim()) { setErr('Enter both a username and an NPC name.'); return; }
    setBusy(true); setErr(''); setNote('');
    try {
      const r = await axios.post(`${API}/admin/npc_affection/set`, {
        requester,
        target: target.trim(),
        npc: npc.trim(),
        affection: affection === '' ? null : Math.max(0, parseInt(affection, 10) || 0),
        dislike: dislike === '' ? null : Math.max(0, parseInt(dislike, 10) || 0),
      });
      setCurrent({
        affection: r.data?.affection ?? 0,
        dislike: r.data?.dislike ?? 0,
        romance_unlocked: (r.data?.affection ?? 0) >= 10,
        canonical_target: r.data?.target,
        npc_key: r.data?.npc,
      });
      setNote(`Saved. ${r.data?.npc} now feels ${r.data?.affection}♥ / ${r.data?.dislike}⚔ toward ${r.data?.target}.`);
    } catch (e) {
      const d = e && e.response && e.response.data && e.response.data.detail;
      setErr(d || 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div style={overlay} data-testid="admin-edit-npc-affection-modal" onClick={onClose}>
      <div style={shell} onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          data-testid="admin-npc-affection-close"
          style={closeX}
        >✕</button>
        <div style={{ fontSize: 18, fontWeight: 'bold', textTransform: 'uppercase',
                      letterSpacing: 2, marginBottom: 4 }}>
          ♥ NPC Affection — View &amp; Edit
        </div>
        <div style={{ fontSize: 11, marginBottom: 12, fontStyle: 'italic', opacity: 0.85 }}>
          Sets how an NPC / Monster / Pokémorph feels toward a specific user.
          Affection ≥ 10 unlocks Romance / Erotic RP with Champion-rank users.
        </div>

        <div style={row}>
          <span style={label}>Target user</span>
          <input
            data-testid="admin-npc-affection-target"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="e.g. testuser"
            style={input}
            autoFocus
          />
        </div>
        <div style={row}>
          <span style={label}>NPC name</span>
          <input
            data-testid="admin-npc-affection-npc"
            value={npc}
            onChange={(e) => setNpc(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="e.g. Anu or Sylveon"
            style={input}
          />
          <button
            data-testid="admin-npc-affection-load"
            onClick={load}
            disabled={loading || !target.trim() || !npc.trim()}
            style={{
              background: '#4a3a1a', color: '#ffe1a0',
              border: '1px solid #a08a30', padding: '4px 14px',
              fontWeight: 'bold', cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.7 : 1,
            }}
          >{loading ? '…' : 'LOAD'}</button>
        </div>

        {current && (
          <div
            data-testid="admin-npc-affection-view"
            style={{
              margin: '8px 0 14px', padding: 10,
              background: '#faf3d8', border: '1px solid #a08a30',
              borderRadius: 4, fontSize: 12,
            }}
          >
            <div>
              <b>{current.npc_key}</b>'s feelings toward <b>{current.canonical_target}</b>:
            </div>
            <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              <span style={readonlyPill} data-testid="admin-npc-affection-view-aff">
                ♥ Affection <b>{current.affection}</b>
              </span>
              <span style={readonlyPill} data-testid="admin-npc-affection-view-dis">
                ⚔ Dislike <b>{current.dislike}</b>
              </span>
              <span
                data-testid="admin-npc-affection-view-romance"
                style={{
                  ...readonlyPill,
                  background: current.romance_unlocked ? '#d6336c' : '#c8c8b8',
                  color: current.romance_unlocked ? '#fff' : '#3a2b14',
                }}
              >{current.romance_unlocked ? 'ROMANCE UNLOCKED' : 'romance locked'}</span>
            </div>
          </div>
        )}

        <div style={{ borderTop: '1px dashed #7a5a1a', paddingTop: 10, marginTop: 6 }}>
          <div style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
            Set values
          </div>
          <div style={row}>
            <span style={label}>Affection ♥</span>
            <input
              data-testid="admin-npc-affection-aff-input"
              type="number" min="0"
              value={affection}
              onChange={(e) => setAffection(e.target.value)}
              placeholder="e.g. 10 (unlocks romance)"
              style={input}
            />
          </div>
          <div style={row}>
            <span style={label}>Dislike ⚔</span>
            <input
              data-testid="admin-npc-affection-dis-input"
              type="number" min="0"
              value={dislike}
              onChange={(e) => setDislike(e.target.value)}
              placeholder="e.g. 0"
              style={input}
            />
          </div>
        </div>

        {err && (
          <div data-testid="admin-npc-affection-error"
               style={{ color: '#7a1f1f', fontSize: 12, marginTop: 6 }}>{err}</div>
        )}
        {note && (
          <div data-testid="admin-npc-affection-note"
               style={{ color: '#1c5a1c', fontSize: 12, marginTop: 6 }}>{note}</div>
        )}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 12 }}>
          <button
            onClick={onClose}
            style={{
              background: '#5a4a1d', color: '#f5e6c0',
              border: '1px solid #3a2b14', padding: '4px 14px',
              fontWeight: 'bold', cursor: 'pointer',
            }}
          >Close</button>
          <button
            data-testid="admin-npc-affection-save"
            onClick={save}
            disabled={busy || !target.trim() || !npc.trim()}
            style={{
              background: '#4a3a1a', color: '#ffe1a0',
              border: '1px solid #a08a30', padding: '4px 14px',
              fontWeight: 'bold', cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.7 : 1,
            }}
          >{busy ? 'Saving…' : 'SAVE'}</button>
        </div>
      </div>
    </div>
  );
};
